import * as React from "react";

import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  Link,
  Accordion as MuiAccordion,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useLocation } from "react-router-dom";

import supportIcon from "client/assets/support-icon.png";
import { useContactUs } from "client/context/contact-us-context";
import { useBranding } from "client/hooks/useBranding";
import { useMerchantModel } from "client/hooks/useMerchantModel";
import { useTranslation } from "client/hooks/useTranslation";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { CountryList, SalesChannel } from "client/lib/apt-merchant";
import { L10n } from "client/utils/branding";
import { Translate } from "client/utils/internationalization";

import {
  FAQ,
  FaqGroup,
  FaqGroupMap,
  getAboutAfterpayFaqs,
  getAddCustomerSupportFaqs,
  getInStoreFaqs,
  getLinkYourBankFaqs,
  getYourBusinessDetailsFaqs,
  useStyles,
} from ".";

const formstackLink =
  "https://afterpay.formstack.com/forms/global_merchant_technical_support";

const TechnicalSupportLink: React.FC = () => {
  const classes = useStyles();
  const t = useTranslation();
  return (
    <Box display="flex" justifyContent="center" px={{ xs: 3 }}>
      <img
        src={supportIcon}
        alt="support chat icon"
        className={classes.supportIcon}
      />
      <Typography className={classes.link} variant="body2">
        <Link
          color="inherit"
          href={formstackLink}
          rel="noopener"
          target="_blank"
          onClick={() =>
            logAmplitudeEvent(
              "ONBOARDING_FAQ_MODAL",
              "technical_support_link_clicked"
            )
          }
        >
          {t("uiFaqContactSupport")}
        </Link>
      </Typography>
    </Box>
  );
};

export const getFaqMap = ({
  branding,
  classes,
  country,
  salesChannel = "Online Only",
  t,
}: {
  branding: L10n;
  country?: CountryList;
  classes: ReturnType<typeof useStyles>;
  salesChannel?: SalesChannel;
  t: Translate;
}): [string, FAQ[] | undefined][] => {
  let base: FaqGroupMap = {};
  if (salesChannel === "In-store Only") {
    base = {
      "": getInStoreFaqs(branding, salesChannel, t, classes),
    };
  } else if (salesChannel === "Online and In-store") {
    const key =
      country === "United Kingdom" ? "About Clearpay" : "About Afterpay";
    base = {
      [key]: getAboutAfterpayFaqs(t, classes),
      "Your business details": getYourBusinessDetailsFaqs(t, classes),
      "Link your bank": getLinkYourBankFaqs(branding, t),
      "Add customer support": getAddCustomerSupportFaqs(t),
      "In-store": getInStoreFaqs(branding, salesChannel, t, classes),
    };
  } else {
    const key =
      country === "United Kingdom" ? "About Clearpay" : "About Afterpay";
    base = {
      [key]: getAboutAfterpayFaqs(t, classes),
      "Your business details": getYourBusinessDetailsFaqs(t, classes),
      "Link your bank": getLinkYourBankFaqs(branding, t),
      "Add customer support": getAddCustomerSupportFaqs(t),
    };
  }
  const ret = Object.entries(base);
  return ret;
};

interface Props {
  answer: React.ReactNode;
  expanded: boolean;
  handleChange: () => void;
  idx: number;
  question: string;
}

const AccordionFaq: React.FC<Props> = ({
  answer,
  expanded,
  handleChange,
  idx,
  question,
}) => {
  const classes = useStyles();
  if (!question) {
    return null;
  }

  if (typeof answer === "string") {
    answer = (
      <Typography className={classes.paragraph} variant="body2">
        {answer}
      </Typography>
    );
  }

  return (
    <MuiAccordion
      classes={{
        expanded: classes.accordionExpanded,
      }}
      expanded={expanded}
      onChange={handleChange}
    >
      <AccordionSummary
        aria-controls={`panel-${idx}-content`}
        classes={{
          content: classes.accordionSummaryContent,
          expandIcon: classes.accordionExpandIcon,
        }}
        expandIcon={<ExpandMoreIcon fontSize="medium" />}
        id={`panel-${idx}-header`}
      >
        <Typography variant="body2" className={classes.question}>
          {question}
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails} role="region">
        <Box>{answer}</Box>
      </AccordionDetails>
    </MuiAccordion>
  );
};

export const isOnStatusOrOnboardingRoute = (pathname: string): boolean =>
  isOnOnboardingRoute(pathname) || isOnStatusRoute(pathname);
export const isOnStatusRoute = (pathname: string): boolean =>
  /status/i.test(pathname);
export const isOnOnboardingRoute = (pathname: string): boolean =>
  /onboarding/i.test(pathname);

export const Faqs: React.FC<{
  showContactUs: boolean;
  faqOpen: boolean;
  setFaqOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ showContactUs, faqOpen, setFaqOpen }) => {
  const [expanded, setExpanded] = React.useState("");
  const { setContactUsOpen } = useContactUs();
  const branding = useBranding();
  const classes = useStyles();
  const location = useLocation();
  const { merchant } = useMerchantModel();
  const t = useTranslation();

  const country = merchant.companyInformation.registeredCountry.value;
  const salesChannel = merchant.lead.salesChannel.value;

  const faqMap = getFaqMap({
    branding,
    classes,
    country,
    salesChannel,
    t,
  });

  if (!isOnStatusOrOnboardingRoute(location.pathname)) {
    return null;
  }

  const handleContactUsClick = () => {
    // legacy event but keeping this to not break previous event tracking
    logAmplitudeEvent("ONBOARDING_FAQ_MODAL", "contact_us_open", {
      source: "faq",
    });
    logAmplitudeEvent("ONBOARDING_CONTACT_US", "contact_us_open", {
      source: "faq",
    });
    setContactUsOpen("faq");
  };

  const getFaqState = (groupHeader: string, question: string) =>
    `${groupHeader}:${question}`;

  const isFaqBeingClosed = (currState: string, updatedState: string) =>
    currState === updatedState;

  const handleChange = (group: FaqGroup, question: string) => () => {
    const faqState = getFaqState(group, question);
    logAmplitudeEvent(
      "ONBOARDING_FAQ_MODAL",
      isFaqBeingClosed(expanded, faqState)
        ? "faq_clicked_closed"
        : "faq_clicked_opened",
      { faq: faqState }
    );
    setExpanded((currentState) => {
      const closedState = "";
      return isFaqBeingClosed(currentState, faqState) ? closedState : faqState;
    });
  };

  return (
    <Box>
      <Fab
        color="primary"
        size="large"
        className={`${classes.iconButton} ${classes.faqButton}`}
        onClick={() => {
          setFaqOpen(true);
          logAmplitudeEvent("ONBOARDING_FAQ_MODAL", "faq_modal_opened", {
            showContactUs,
          });
        }}
      >
        ?
      </Fab>
      <Dialog
        aria-describedby="frequently asked questions"
        aria-labelledby="faq-modal"
        className={classes.modalRoot}
        fullWidth
        maxWidth="md"
        onClose={() => setFaqOpen(false)}
        open={faqOpen}
      >
        <Box>
          <Box display="flex" justifyContent="flex-end" m={3}>
            <CloseIcon
              className={classes.iconButton}
              onClick={() => setFaqOpen(false)}
            />
          </Box>
          <Box pl={{ lg: 12 }} pr={{ lg: 20 }} mb={9}>
            <DialogTitle id="faq-modal">
              <Box display="flex" justifyContent="center">
                <Typography variant="h5">{t("uiFaqHeader")}</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box mb={{ xs: 3, md: 20 }}>
                {faqMap.map(([group, faqs], i) => (
                  <Box key={`${group}_${i}`} mb={2}>
                    <Typography variant="h6">
                      <strong className={classes.sectionHeader}>{group}</strong>
                    </Typography>
                    {faqs &&
                      faqs.map((faq, i) => (
                        <AccordionFaq
                          expanded={
                            expanded === getFaqState(group, faq.question)
                          }
                          key={`${faq.question}_${i}`}
                          handleChange={handleChange(
                            group as FaqGroup,
                            faq.question
                          )}
                          idx={i}
                          {...faq}
                        />
                      ))}
                  </Box>
                ))}
              </Box>
            </DialogContent>
            {showContactUs ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems={"center"}
                px={{ xs: 3, sm: 20 }}
              >
                <Button
                  onClick={handleContactUsClick}
                  variant="outlined"
                  size="medium"
                  color="secondary"
                  fullWidth
                >
                  Contact Us
                </Button>
              </Box>
            ) : (
              <TechnicalSupportLink />
            )}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};
